/**
 * LocalityHeader — bold, colorful chip strip shown above the tagline.
 *
 * Renders "📍 City · State · Country · Postal" as separate chips. Each
 * chip is a <Link> to its own location article (`/l/{country}/{state}/{city}`)
 * so the reader can drill into any level.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Edit2 } from "lucide-react";
import { useLocality } from "@/contexts/LocalityContext";
import LocalityDialog from "@/components/LocalityDialog";
import { titleCase } from "@/lib/titleCase";

function slugify(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function href({ country, state, city }) {
  const parts = [country, state, city].filter(Boolean).map(slugify);
  return "/l/" + parts.join("/");
}

export default function LocalityHeader() {
  const { locality, status } = useLocality();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setDialogOpen(true);
    window.addEventListener("sp-open-locality-dialog", onOpen);
    return () => window.removeEventListener("sp-open-locality-dialog", onOpen);
  }, []);

  if (status === "loading") return null;

  return (
    <>
      <div
        data-testid="locality-header"
        className="max-w-6xl mx-auto px-6 pt-5 pb-2"
      >
        {locality ? (
          <>
            {/* Caption row */}
            <div
              className="mb-2 text-sm sm:text-base font-mono uppercase tracking-widest font-semibold"
              style={{ color: "#2563EB" }}
              data-testid="locality-caption"
            >
              Click on each tab to learn more &rsaquo;
            </div>

            {/* Chip strip — each chip is a link */}
            <div className="flex items-center flex-wrap gap-2 text-lg">
              <MapPin className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
              {locality.city && (
                <>
                  <ChipLink
                    to={href({ country: locality.country, state: locality.state, city: locality.city })}
                    label={titleCase(locality.city)}
                    testid="locality-chip-city"
                    primary
                  />
                  <ChipSep />
                </>
              )}
              {locality.state && (
                <>
                  <ChipLink
                    to={href({ country: locality.country, state: locality.state })}
                    label={titleCase(locality.state)}
                    testid="locality-chip-state"
                  />
                  <ChipSep />
                </>
              )}
              {locality.country && (
                <ChipLink
                  to={href({ country: locality.country })}
                  label={titleCase(locality.country)}
                  testid="locality-chip-country"
                />
              )}
              {locality.postal_code && (
                <>
                  <ChipSep />
                  <span
                    data-testid="locality-chip-postal"
                    className="px-3 py-1 rounded-full font-mono text-xs"
                    style={{ background: "var(--wofy-surface)", color: "var(--wofy-text)", border: "1px solid var(--wofy-border)" }}
                  >{locality.postal_code}</span>
                </>
              )}
              <button
                data-testid="locality-change-btn"
                onClick={() => setDialogOpen(true)}
                className="ml-2 text-xs inline-flex items-center gap-1 hover:underline"
                style={{ color: "var(--wofy-text-soft)" }}
              >
                <Edit2 className="w-3 h-3" /> Change
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center flex-wrap gap-2">
            <MapPin className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
            <button
              data-testid="locality-set-btn"
              onClick={() => setDialogOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-base shadow-sm hover:shadow-md transition-all animate-pulse"
              style={{
                background: "linear-gradient(90deg, var(--wofy-primary) 0%, #f97316 100%)",
                color: "#FFFFFF",
              }}
            >
              <MapPin className="w-4 h-4" />
              Set your location
              <span className="text-xs opacity-90 font-normal">— tap to personalize</span>
            </button>
          </div>
        )}
      </div>

      {dialogOpen && <LocalityDialog onClose={() => setDialogOpen(false)} />}
    </>
  );
}

function ChipLink({ to, label, testid, primary = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="px-4 py-1.5 rounded-full font-semibold text-lg lg:text-xl hover:opacity-90 hover:-translate-y-0.5 transition-all"
      style={{
        background: primary ? "var(--wofy-primary)" : "var(--wofy-surface)",
        color: primary ? "#FDFBF7" : "var(--wofy-text)",
        border: primary ? "none" : "1px solid var(--wofy-border)",
      }}
    >
      {label}
    </Link>
  );
}

function ChipSep() {
  return <span className="opacity-40 text-xl" style={{ color: "var(--wofy-text-soft)" }}>·</span>;
}
